import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@ui';
import { X } from '@ui/icons';
import { t } from '@shared/i18n';

export interface SplitChoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when user chooses to assign items to specific people. */
  onAssignItems: () => void;
  /** Called when user chooses to split total evenly (existing behaviour). */
  onSplitTotal: () => void;
}

/** Render the description, bolding the emphasised substring. */
function renderDescription(text: string, emphasis: string) {
  const i = text.indexOf(emphasis);
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <strong className="font-semibold text-fg-strong">{emphasis}</strong>
      {text.slice(i + emphasis.length)}
    </>
  );
}

/**
 * Confirmation dialog shown after saving breakdown items.
 * Asks whether to assign each item to specific people or split the total evenly.
 */
export function SplitChoiceModal({
  open,
  onOpenChange,
  onAssignItems,
  onSplitTotal,
}: SplitChoiceModalProps) {
  const c = t.newBill.splitChoiceModal;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-70 bg-black/50" />
        <Dialog.Content
          aria-describedby="split-choice-desc"
          className="fixed left-1/2 top-1/2 z-70 w-[calc(100vw-2rem)] max-w-120 -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-surface-raised shadow-overlay focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
            <Dialog.Title className="text-base font-semibold text-fg-strong leading-snug">
              {c.title}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                id="split-choice-close-btn"
                type="button"
                aria-label={c.close}
                className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-border-soft hover:bg-border focus-visible:outline-none"
              >
                <X className="size-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <p id="split-choice-desc" className="px-5 pb-5 text-sm text-fg leading-relaxed">
            {renderDescription(c.description.text, c.description.emphasis)}
          </p>

          {/* Divider + Buttons */}
          <div className="border-t border-border-soft px-5 py-4 flex flex-wrap items-center gap-3">
            <Button
              id="split-choice-assign-btn"
              variant="primary"
              size="md"
              onClick={() => {
                onOpenChange(false);
                onAssignItems();
              }}
            >
              {c.giveItemsToPeople}
            </Button>
            <Button
              id="split-choice-total-btn"
              variant="secondary"
              size="md"
              onClick={() => {
                onOpenChange(false);
                onSplitTotal();
              }}
            >
              {c.splitTotalEvenly}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
